import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, ChevronDown, ChevronRight, BookOpen, Shield, Gamepad2, MessageSquare, Users, FileText, Map, Heart, Pen, Camera, Volume2, Award, Settings, Clock, Brain, Lock, Globe, AlertTriangle, LifeBuoy, Fingerprint, Smartphone } from 'lucide-react';
import EmergencyButton from '../components/EmergencyButton';
import Footer from '../components/Footer';
import { BackToTopButton } from '../components/NavigationHelpers';
import useTheme4A from '../components/useTheme4A';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function Section({ id, icon: Icon, title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div id={id} className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-zinc-50 transition-colors"
        data-testid={`manual-section-${id}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-blue-600" />
          </div>
          <span className="text-sm font-semibold text-zinc-900">{title}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-zinc-700 leading-relaxed border-t border-zinc-100">
          {children}
        </div>
      )}
    </div>
  );
}

function NavStep({ from, action, to }) {
  return (
    <div className="flex items-center gap-2 text-xs bg-zinc-50 rounded-lg px-3 py-2 my-1">
      <span className="font-medium text-zinc-600">{from}</span>
      <ChevronRight className="w-3 h-3 text-zinc-400 flex-shrink-0" />
      <span className="text-blue-600 font-medium">{action}</span>
      <ChevronRight className="w-3 h-3 text-zinc-400 flex-shrink-0" />
      <span className="font-medium text-zinc-600">{to}</span>
    </div>
  );
}

function UserManualPage() {
  useTheme4A();
  const navigate = useNavigate();

  const toc = [
    { id: 'disclaimer', label: 'Medical & Legal Disclaimer' },
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'passkeys', label: 'Passkeys & Multi-device Sign-in' },
    { id: 'navigation', label: 'Navigation & Layout' },
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'daily-tasks', label: 'Daily Tasks' },
    { id: 'weekly-tasks', label: 'Weekly Tasks' },
    { id: 'content-library', label: 'Content Library & Readings' },
    { id: 'tts', label: 'Listen to Readings (TTS)' },
    { id: 'lessons', label: 'Lessons' },
    { id: 'self-work', label: 'Self-Work' },
    { id: 'meetings', label: 'Meeting Log & Verification' },
    { id: 'bot-chat', label: 'Chat with Your Sponsor Bot' },
    { id: 'games', label: 'Games & Anti-Addiction Timer' },
    { id: 'group-chat', label: 'Recovery Room (Peer Chat)' },
    { id: 'badges', label: 'Badges & Achievements' },
    { id: 'journey', label: 'Journey & Progress' },
    { id: 'women-only', label: 'Women-Only Safe Space' },
    { id: 'men-only', label: 'Men-Only Safe Space' },
    { id: 'profile', label: 'Profile & Settings' },
    { id: 'personal-history', label: 'Personal History' },
    { id: 'superadmin', label: 'SuperAdmin Panel' },
    { id: 'back-arrows', label: 'Back Arrow Behaviour' },
    { id: 'emergency', label: 'SOS Emergency Button (Manual: no auto-alerts)' },
    { id: 'mental-health-redflag', label: 'Mental Health Check-ins & Red Flag Auto-Alerts' },
  ];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b border-zinc-200 z-40 shadow-sm">
        <div className="h-14 px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded-md hover:bg-zinc-100" data-testid="back-button">
              <ArrowLeft className="w-5 h-5 text-zinc-700" />
            </button>
            <button onClick={() => navigate('/dashboard')} className="p-1.5 rounded-md hover:bg-zinc-100" data-testid="home-button">
              <Home className="w-5 h-5 text-zinc-500" />
            </button>
          </div>
          <img src={AXIOM_LOGO} alt="Axiom" className="h-7 absolute left-1/2 transform -translate-x-1/2" />
          <EmergencyButton />
        </div>
      </header>

      <main className="pt-18 px-4 pb-6">
        <div className="max-w-2xl mx-auto pt-4">
          {/* Title */}
          <div className="mt-12 mb-6">
            <h1 className="text-2xl font-bold text-zinc-950 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              User Manual
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Everything you need to know about the Axiom Recovery Programme</p>
          </div>

          {/* Table of Contents */}
          <div className="bg-white border border-zinc-200 rounded-xl p-5 mb-6">
            <h2 className="text-sm font-bold text-zinc-800 uppercase tracking-wider mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-blue-600" />
              Contents
            </h2>
            <nav className="grid grid-cols-1 gap-1">
              {toc.map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="text-left text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md px-2 py-1.5 transition-colors"
                >
                  {i + 1}. {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Sections */}
          <div className="space-y-3">
            {/* 1. Disclaimer */}
            <div id="disclaimer" className="bg-red-50 border-2 border-red-300 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-sm font-bold text-red-900 uppercase tracking-wider mb-2">Medical & Legal Disclaimer</h2>
                  <div className="text-xs text-red-800 leading-relaxed space-y-2">
                    <p><strong>READ THIS BEFORE USING THE PROGRAMME.</strong></p>
                    <p>This programme is a Recovery support tool. It does <strong>NOT</strong> replace professional medical care, diagnosis, treatment or supervised detoxification.</p>
                    <p><strong>Emergency:</strong> Call 911 (or your local emergency number) for acute withdrawal, medical emergencies or suicidal thoughts.</p>
                    <p><strong>Medical Supervision:</strong> Use this programme WITH (not instead of) licensed healthcare professionals.</p>
                    <p><strong>No Liability:</strong> Axiom is not liable for medical complications, relapse, injury or death. Participation is at your own risk.</p>
                    <p><strong>Criminal Justice:</strong> This programme generates reports for courts/probation. You consent to data collection and reporting.</p>
                    <p className="font-bold">By using this app, you acknowledge and accept these terms.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Getting Started */}
            <Section id="getting-started" icon={Lock} title="1. Getting Started" defaultOpen>
              <div className="pt-3 space-y-3">
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Invite Code</h4>
                  <p>The landing page requires an invite code to proceed. This ensures only authorised participants can join. If you already have an account, tap <strong>"Already have an account? Log in"</strong> to go directly to the login screen.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Create Your Account</h4>
                  <p>Register with your email and a password (minimum 8 characters). You will also be asked to set a <strong>4-digit PIN</strong> for quick future logins.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Onboarding</h4>
                  <p>After registration you will be guided through onboarding where you can:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li>Customise your AI sponsor bot's name and gender preference</li>
                    <li>Choose your preferred language</li>
                    <li>Set your sobriety date and time (this powers the sobriety clock on your dashboard)</li>
                    <li>Select your primary drug of choice</li>
                  </ul>
                </div>
                <NavStep from="Landing Page" action="Enter invite code" to="Registration / Login" />
              </div>
            </Section>

            {/* 1A. Install on your phone (PWA / Add to Home Screen) */}
            <Section id="install-on-phone" icon={Smartphone} title="1A. Install this App on Your Phone">
              <div className="pt-3 space-y-4">
                <p>
                  You can install Axiom directly from your browser: no App Store or Google Play needed.
                  Once installed, it opens like any other app on your home screen, launches full-screen and stays signed in.
                </p>

                <div className="rounded-lg border border-zinc-200 overflow-hidden">
                  <div className="bg-zinc-50 px-4 py-2 border-b border-zinc-200">
                    <h4 className="font-semibold text-zinc-800 text-sm flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-blue-600" />
                      iPhone &amp; iPad (Safari)
                    </h4>
                  </div>
                  <ol className="px-5 py-3 list-decimal space-y-1.5 text-sm">
                    <li>Open Axiom in <strong>Safari</strong> (this only works in Safari, not Chrome on iOS).</li>
                    <li>Tap the <strong>Share</strong> button: the square with an up-arrow, at the bottom of the screen.</li>
                    <li>Scroll down and tap <strong>“Add to Home Screen”</strong>.</li>
                    <li>Give it a name (or leave it as “Axiom”) and tap <strong>Add</strong>.</li>
                    <li>The Axiom icon now appears on your home screen. Tap it to open the app.</li>
                  </ol>
                </div>

                <div className="rounded-lg border border-zinc-200 overflow-hidden">
                  <div className="bg-zinc-50 px-4 py-2 border-b border-zinc-200">
                    <h4 className="font-semibold text-zinc-800 text-sm flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-green-600" />
                      Android (Chrome)
                    </h4>
                  </div>
                  <ol className="px-5 py-3 list-decimal space-y-1.5 text-sm">
                    <li>Open Axiom in <strong>Chrome</strong>.</li>
                    <li>Tap the <strong>⋮ menu</strong> (three dots) in the top-right corner.</li>
                    <li>Tap <strong>“Install app”</strong> or <strong>“Add to Home screen”</strong>.</li>
                    <li>Confirm with <strong>Install</strong> / <strong>Add</strong>.</li>
                    <li>The Axiom icon now appears on your home screen. Tap it to open the app.</li>
                  </ol>
                </div>

                <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3">
                  <strong>Note:</strong> This is a Progressive Web App: the same Axiom you use in your browser,
                  just shortcut-installed. Sign-in, journals, meetings, everything works the same. Updates are automatic.
                </p>

                <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-1.5">
                  <p><strong>What's new:</strong> If you're on a supported browser (Chrome, Edge, Safari on iOS) you'll see a small <em>“Install Axiom on your home screen”</em> banner at the bottom of the screen when you first arrive.</p>
                  <ul className="list-disc ml-5 space-y-0.5">
                    <li><strong>Android / Chrome / Edge:</strong> tap <strong>Install</strong> in the banner: the OS install dialog appears; confirm and Axiom lands on your home screen.</li>
                    <li><strong>iPhone / iPad (Safari):</strong> the banner walks you through the three-step Share → Add to Home Screen flow. Follow the on-screen prompts.</li>
                    <li>You can dismiss the banner at any time (× icon). It will not re-appear for 30 days on the same browser.</li>
                    <li>Once installed, the launcher icon opens the app full-screen with no browser bars, remembers your sign-in and starts noticeably faster on repeat visits (an offline-ready cache is registered on install).</li>
                  </ul>
                </div>
              </div>
            </Section>

            {/* Passkeys & Multi-device Sign-in */}
            <Section id="passkeys" icon={Fingerprint} title="2. Passkeys & Multi-device Sign-in">
              <div className="pt-3 space-y-3">
                <p>
                  Passkeys let you sign in with your face, fingerprint or device PIN: no
                  password needed. They're faster and safer than passwords and can't be
                  guessed or phished. Your password keeps working as a backup.
                </p>

                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Set up a passkey on your first device</h4>
                  <ol className="list-decimal ml-5 space-y-1">
                    <li>Sign in with your email and password as normal.</li>
                    <li>You'll see a one-time prompt: <strong>"Skip the password next time? Use Face ID / Fingerprint."</strong> Tap <strong>Set up Face ID / Fingerprint</strong>.</li>
                    <li>Approve the prompt your phone or laptop shows you (Face ID, Touch ID, Windows Hello or your device PIN).</li>
                    <li>That's it. Next time you sign in, just enter your email and tap <strong>"Sign in with Face ID / Fingerprint"</strong>.</li>
                  </ol>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <h4 className="font-bold text-blue-900 mb-1.5">Register more than one device (recommended)</h4>
                  <p className="text-xs text-blue-900 leading-relaxed mb-2">
                    Adding a passkey on each device you use means you can sign in with one tap
                    everywhere: phone, tablet, laptop. If you ever lose a device you can also
                    revoke it without affecting the others.
                  </p>
                  <ol className="list-decimal ml-5 text-xs text-blue-900 space-y-1">
                    <li>On the new device (e.g. your iPad or laptop), open the app and sign in with your email and password.</li>
                    <li>Go to <strong>Profile</strong> in the bottom menu.</li>
                    <li>Scroll to the <strong>Passkeys</strong> card and tap <strong>"Add another device"</strong>.</li>
                    <li>Give it a name you'll remember (e.g. "iPad", "Work laptop") and approve the device prompt.</li>
                    <li>Repeat on every device you sign in from.</li>
                  </ol>
                </div>

                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">If you share a device family (iPhone + iPad + Mac)</h4>
                  <p>
                    Apple devices using the same iCloud account share passkeys automatically through
                    iCloud Keychain: you only need to enrol once. Same for Android/Chrome devices on
                    the same Google account. You can still add an explicit passkey per device if you'd
                    like more control.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Removing a passkey (lost or stolen device)</h4>
                  <p>
                    Open <strong>Profile → Passkeys</strong>, find the device in the list and tap the
                    bin icon next to it. That device can no longer use Face ID / Fingerprint to sign in,
                    but your password still works on every device.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">If passkeys aren't an option for you</h4>
                  <p>
                    No problem: keep using your email and password or your 4-digit PIN. Passkeys are
                    purely an extra option to make sign-in easier; nothing about the programme changes.
                  </p>
                </div>

                <NavStep from="Profile" action="Passkeys card" to="Add another device" />
              </div>
            </Section>

            {/* 3. Navigation & Layout */}
            <Section id="navigation" icon={Map} title="2. Navigation & Layout: Where to Find Everything">
              <div className="pt-3 space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <h4 className="font-bold text-blue-900 mb-1.5">Quick navigation map</h4>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>📚 <strong>Programme readings & lessons</strong> → Library tab → <em>Programme</em> sub-tab</li>
                    <li>🍳 <strong>Diet & Recovery (200 recipes + meal planner)</strong> → Library tab → <em>Diet</em> sub-tab</li>
                    <li>💬 <strong>Recovery Room (peer chat: Country / Global / Women / Men rooms)</strong> → Dashboard → <em>Recovery Room</em> card (unlocks after the 2-Week Window: NOT Week 4)</li>
                    <li>🎯 <strong>Self-Work tasks</strong> → Dashboard → <em>Daily Tasks</em></li>
                    <li>🆘 <strong>Relapse Recovery (after a relapse)</strong> → Dashboard → <em>Relapse Recovery</em> card OR Profile → <em>Relapse Recovery</em></li>
                    <li>💗 <strong>Women-Only Safe Space</strong> → Dashboard → <em>Women-Only</em> card OR Profile (visible only to female users)</li>
                    <li>🧭 <strong>Men-Only Safe Space</strong> → Dashboard → <em>Men-Only</em> card OR Profile (visible only to male users)</li>
                    <li>🚨 <strong>SOS Emergency Button (manual dialler: does NOT notify anyone)</strong> → Top-right red button on every page. Surfaces 000 / 911 / 112 / etc. and crisis hotlines. <em>You</em> tap to call.</li>
                    <li>❤️ <strong>Mental Health Check-ins (Wellbeing scores stored locally to your account)</strong> → Dashboard → <em>Mental Health</em> card. The check-in surfaces crisis lines to you when scores are high; no third-party notification is sent from this feature.</li>
                    <li>📋 <strong>Personal History assessment</strong> → Profile → <em>Personal History</em></li>
                    <li>🗂️ <strong>Case Management (court reports, drug screens)</strong> → Profile → <em>Case Management</em></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Bottom Navigation Bar</h4>
                  <p>The bottom bar on the Dashboard has five tabs:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li><strong>Dashboard</strong> -- Your home screen with tasks, sobriety clock and quick actions</li>
                    <li><strong>Library</strong> -- Content Library with the Programme tab (readings + lessons), Diet tab and Resources</li>
                    <li><strong>Inbox</strong> -- Admin messages and notifications</li>
                    <li><strong>Group</strong> -- Recovery Room peer chat (unlocks after the 2-Week Window: NOT Week 4)</li>
                    <li><strong>Journey</strong> -- Your full programme timeline, week-by-week progress</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Top Header</h4>
                  <p>Every page shows a consistent header with:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li><strong>Back arrow (left)</strong> -- Returns to the relevant list (Programme readings → programme list, lessons → lesson list, etc.)</li>
                    <li><strong>Home icon</strong> -- Always takes you straight to the Dashboard</li>
                    <li><strong>Emergency button (right)</strong> -- Instant access to crisis resources</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Avatar Menu (top-right on Dashboard)</h4>
                  <p>Tap your avatar to access:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li>Profile (with Relapse Recovery, Case Management, Personal History links inside)</li>
                    <li>User Manual (this page)</li>
                    <li>Legal documents (Terms, Privacy Policy, HIPAA)</li>
                    <li>References & Documentation</li>
                    <li>SuperAdmin panel (admins only)</li>
                    <li>Log Out</li>
                  </ul>
                </div>
              </div>
            </Section>

            {/* Relapse Recovery navigation */}
            <Section id="relapse-recovery" icon={LifeBuoy} title="2A. If You Relapse: How to Get Help Inside the App">
              <div className="pt-3 space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900">
                  <p className="font-bold mb-1">Relapse is a medical event, not a moral failure.</p>
                  <p className="text-xs leading-relaxed">Alcoholism/addiction is a chronic, relapsing disease. The most important thing after a relapse is your immediate safety: and getting back into the programme as soon as you're medically stable.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Two ways to reach Relapse Recovery</h4>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li><strong>Dashboard</strong> → tap the <em>Relapse Recovery</em> card (amber, with the life-buoy icon)</li>
                    <li><strong>Profile</strong> → scroll to <em>Relapse Recovery</em></li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">What you'll find there</h4>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    <li><strong>Step 1: Get medically safe:</strong> emergency numbers (000 / 911 / 112), guidance to speak to a doctor or hospital before you do anything else and a reminder to call your sponsor.</li>
                    <li><strong>Step 2: Urine Drug Screen (UDS):</strong> guidance for your re-entry. In Australia, your GP can refer you for a urine drug screen. Ask your GP or the pathology centre whether there is any cost. Other countries: ask your prescribing clinician for a referral. (Full UDS upload + tracking is coming soon.)</li>
                    <li><strong>Step 3: Re-engage gently:</strong> attend a meeting today, re-read the Disease Module, resume daily readings and lessons.</li>
                    <li><strong>Court reports:</strong> any report ARP produces for a Court contains <em>only verifiable facts</em> (engagement metrics, attendance dates, UDS results). No AI-generated opinions or commentary.</li>
                  </ul>
                </div>
              </div>
            </Section>

            {/* 4. Dashboard */}
            <Section id="dashboard" icon={Home} title="3. Dashboard">
              <div className="pt-3 space-y-3">
                <p>The Dashboard is your home base. It shows:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Week Badge</strong> -- Your current Programme Week (of 30) or your Week 1 or 2 of the 2-Week Window during assessment</li>
                  <li><strong>Thought of the Day</strong> -- A daily motivational quote</li>
                  <li><strong>Sobriety Clock</strong> -- Live counter showing days, hours, minutes and seconds since your sobriety date</li>
                  <li><strong>Progress Circle</strong> -- Overall task completion percentage for the current week</li>
                  <li><strong>Badges</strong> -- Your most recently earned achievement badges</li>
                  <li><strong>Daily Tasks</strong> -- Expandable section showing today's Journal and Mental Health check-in</li>
                  <li><strong>Weekly Tasks</strong> -- Expandable section showing meeting requirements, readings and self-work</li>
                  <li><strong>Chat with Bot</strong> -- Quick button to open your AI sponsor chat</li>
                  <li><strong>Games</strong> -- Quick button to access Recovery-friendly games</li>
                </ul>
                <NavStep from="Any page" action="Tap Home icon" to="Dashboard" />
              </div>
            </Section>

            {/* 5. Daily Tasks */}
            <Section id="daily-tasks" icon={Pen} title="4. Daily Tasks">
              <div className="pt-3 space-y-3">
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Journal Entry</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Write daily reflections on your Recovery journey</li>
                    <li>Record your mood score on a 1-10 scale</li>
                    <li>Supports voice-to-text input</li>
                    <li>Minimum <strong>5 entries per week</strong> required for progression</li>
                  </ul>
                  <NavStep from="Dashboard" action="Daily Tasks > Journal" to="Journal Page" />
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Mental Health Check-in</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Daily wellbeing score using a 1-10 slider</li>
                    <li>Optional notes about how you're feeling</li>
                    <li>Minimum <strong>5 check-ins per week</strong> required</li>
                  </ul>
                  <NavStep from="Dashboard" action="Daily Tasks > Mental Health" to="Mental Health Page" />
                </div>
              </div>
            </Section>

            {/* 6. Weekly Tasks */}
            <Section id="weekly-tasks" icon={Clock} title="5. Weekly Tasks">
              <div className="pt-3 space-y-3">
                <p>The Weekly Tasks dropdown on your Dashboard shows:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Meetings</strong> -- 5 TSF meetings per week (AA, NA, ACA, SMART Recovery)</li>
                  <li><strong>Readings</strong> -- Navigate to the Content Library to complete your weekly reading</li>
                  <li><strong>Self-Work</strong> -- Complete assigned self-work tasks (Stickman Assessment, Introduction, etc.)</li>
                </ul>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Week Progression Rules</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Complete 5+ journal entries, 5+ mental health check-ins and 5 meetings to progress</li>
                    <li>Flexible policy: If journal/mental health incomplete BUT meetings done, you can still progress</li>
                    <li>If incomplete for 2 weeks in a row, progress is <strong>stalled</strong> until tasks are completed</li>
                  </ul>
                </div>
              </div>
            </Section>

            {/* 7. Content Library & Readings */}
            <Section id="content-library" icon={BookOpen} title="6. Content Library & Readings">
              <div className="pt-3 space-y-3">
                <p>The Content Library is your central hub for learning materials.</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Weekly Readings</strong> -- Each week has assigned reading material covering Recovery concepts</li>
                  <li><strong>Audio Resources</strong> -- Listen to Recovery content</li>
                  <li><strong>Video Resources</strong> -- Watch educational videos</li>
                </ul>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Reading a Weekly Reading</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>A <strong>reading timer</strong> tracks how long you spend on each reading</li>
                    <li>You must meet the <strong>minimum required time</strong> (shown on-screen) before you can mark the reading as complete</li>
                    <li>You can pause, resume and restart the timer</li>
                    <li>Readings may contain formatted text or a PDF document</li>
                  </ul>
                </div>
                <NavStep from="Dashboard" action="Bottom bar > Library" to="Content Library" />
                <NavStep from="Content Library" action="Tap a reading" to="Reading Page (with timer)" />
              </div>
            </Section>

            {/* 8. TTS */}
            <Section id="tts" icon={Volume2} title="7. Listen to Readings (Text-to-Speech)">
              <div className="pt-3 space-y-3">
                <p>Every reading can be <strong>listened to</strong> in <strong>8 languages</strong> using AI-powered text-to-speech:</p>
                <div className="grid grid-cols-2 gap-1 my-2">
                  {['English', 'Russian', 'Ukrainian', 'Portuguese', 'French', 'Italian', 'Spanish', 'Korean'].map(lang => (
                    <span key={lang} className="text-xs bg-blue-50 text-blue-700 rounded-md px-2 py-1">{lang}</span>
                  ))}
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">How to Use</h4>
                  <ol className="list-decimal ml-5 space-y-1">
                    <li>Open any weekly reading</li>
                    <li>In the <strong>"Listen to Reading"</strong> section, tap the language button to choose your language</li>
                    <li>Tap <strong>"Listen in [Language]"</strong> to generate audio</li>
                    <li>The AI will translate and read the content aloud (generation takes a few seconds)</li>
                    <li>Tap <strong>"Stop Listening"</strong> to pause playback</li>
                  </ol>
                </div>
                <p className="text-xs text-zinc-500">Note: Listening automatically starts the reading timer so your time is counted.</p>
              </div>
            </Section>

            {/* 9. Lessons */}
            <Section id="lessons" icon={FileText} title="8. Lessons">
              <div className="pt-3 space-y-3">
                <p>Structured lesson content covering Recovery theory and practice.</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Introduction to ARP</strong> -- The foundational lesson introducing the Axiom Recovery Programme</li>
                  <li><strong>Weekly Lessons</strong> -- Content aligned with each week of the programme</li>
                  <li>Lessons support TTS playback just like readings</li>
                  <li>PDF documents can be opened directly from the lesson page</li>
                </ul>
                <NavStep from="Dashboard" action="Weekly Tasks > Self-Work" to="Self-Work Page > Lessons" />
              </div>
            </Section>

            {/* 10. Self-Work */}
            <Section id="self-work" icon={Brain} title="9. Self-Work">
              <div className="pt-3 space-y-3">
                <p>Self-work activities assigned to help your Recovery process.</p>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Required Readings Section</h4>
                  <p>Contains programme readings that are part of your weekly assignments.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Self-Work Tasks</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Introduction to ARP</strong> -- Foundational overview of the programme</li>
                    <li><strong>Stickman Assessment</strong> -- A self-assessment tool for understanding patterns</li>
                    <li>Additional tasks are added as you progress through the programme</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">All Programme Items</h4>
                  <p>At the bottom of the Self-Work page, you will find a complete list of all programme items, categorised as Readings and Self-Work. Each item is clickable.</p>
                </div>
                <NavStep from="Dashboard" action="Weekly Tasks > Self-Work" to="Self-Work Page" />
              </div>
            </Section>

            {/* 11. Meeting Log & Verification */}
            <Section id="meetings" icon={Camera} title="10. Meeting Log & Verification">
              <div className="pt-3 space-y-3">
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Meeting Requirement</h4>
                  <p><strong>5 TSF meetings per week</strong> (AA, NA, ACA, SMART Recovery or similar).</p>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">In-Person Meetings</h4>
                  <ol className="list-decimal ml-5 space-y-1">
                    <li>Tap "Log Meeting" on the Meeting Log page</li>
                    <li>Take a <strong>"before" selfie</strong> (before entering the meeting)</li>
                    <li>Take an <strong>"after" selfie</strong> (after leaving the meeting)</li>
                    <li>Select the meeting type</li>
                    <li>The system logs timestamps and photos for verification</li>
                  </ol>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Zoom Meetings (Max 2 per week)</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Provide a verification screenshot showing active participation</li>
                    <li>Limited to 2 per week for criminal justice compliance</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Meeting Verification</h4>
                  <p>Some meetings require location-based check-ins with timed intervals to prove attendance.</p>
                </div>
                <NavStep from="Dashboard" action="Weekly Tasks > Meetings" to="Meeting Log" />
                <NavStep from="Meeting Log" action="Zoom Meetings tab" to="Zoom Meetings" />
              </div>
            </Section>

            {/* 12. Bot Chat */}
            <Section id="bot-chat" icon={MessageSquare} title="11. Chat with Your Sponsor Bot">
              <div className="pt-3 space-y-3">
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Available 24/7</strong> -- Your AI sponsor is always ready to talk</li>
                  <li><strong>TSF Approach</strong> -- Direct, practical Recovery advice. No toxic positivity</li>
                  <li><strong>Conversation History</strong> -- All chats are saved and completely private</li>
                  <li>The bot uses your chosen name and personality from onboarding</li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="font-bold text-blue-900 mb-1">Sponsor bot is automatically gender-matched to you.</p>
                  <p className="text-xs text-blue-800 leading-relaxed">When you complete Personal History, your AI Sponsor's gender is set to match yours (women → female sponsor, men → male sponsor). This is intentional: same-gender sponsorship keeps the lines of communication clean and sets healthy boundaries between men and women in early Recovery. You can override this in Settings, but the default is the safe one.</p>
                </div>
                <NavStep from="Dashboard" action="Chat with [Bot Name]" to="Bot Chat" />
              </div>
            </Section>

            {/* 13. Games */}
            <Section id="games" icon={Gamepad2} title="12. Games & Anti-Addiction Timer">
              <div className="pt-3 space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900">
                  <p className="font-bold mb-1">Heads-up: the games library is being rebuilt.</p>
                  <p className="text-xs leading-relaxed">Some current games have known UX issues and we're building a stronger, more Recovery-aligned set. The anti-addiction timer below is the important part: it stays no matter which games are live.</p>
                </div>
                <p>Recovery-friendly games designed to provide a healthy mental break.</p>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Anti-Addiction Timer</h4>
                  <p>To keep gaming healthy, an anti-addiction timer is built in:</p>
                  <ol className="list-decimal ml-5 space-y-1">
                    <li><strong>Session 1:</strong> 10 minutes of play</li>
                    <li><strong>Break 1:</strong> 30-second cooldown</li>
                    <li><strong>Session 2:</strong> 10 minutes of play</li>
                    <li><strong>Break 2:</strong> 30-second cooldown</li>
                    <li><strong>Session 3:</strong> 10 minutes of play</li>
                    <li><strong>Lockout:</strong> 1-hour lockout after 3 sessions</li>
                  </ol>
                  <p className="mt-2">The timer status bar at the top of the Games page shows remaining time and session number.</p>
                </div>
                <NavStep from="Dashboard" action="Take a Break -- Games" to="Games Page" />
              </div>
            </Section>

            {/* 14. Recovery Room */}
            <Section id="group-chat" icon={Users} title="13. Recovery Room (Peer Chat)">
              <div className="pt-3 space-y-3">
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Unlocks after the 2-Week Window</strong> -- once you transition into Week 1 of the programme</li>
                  <li><strong>Anonymous</strong> -- you choose a pseudonym (e.g. "GraemeT" or "Ipswich"); your real name is never shown</li>
                  <li><strong>Country rooms</strong> -- you auto-join a room for your country (same language, no translation overhead). Toggle to the Global room any time</li>
                  <li><strong>Women-only and Men-only rooms</strong> -- additional gender-gated rooms unlock based on your Personal History. Same-gender peer support, separate from the mixed Country and Global rooms</li>
                  <li><strong>Sentinel AI</strong> -- Messages are monitored automatically; community flags escalate to a SuperAdmin</li>
                </ul>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Recovery Room Rules</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>No glorifying substance use</li>
                    <li>No cross-talk (unsolicited advice to others)</li>
                    <li>Share personal experience only: "I" statements, not "you should"</li>
                    <li>Be respectful and supportive</li>
                    <li>13th-stepping (sexual / romantic approaches to newcomers) is never acceptable</li>
                  </ul>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
                  <p className="font-bold text-rose-900 mb-1">If a message is inappropriate</p>
                  <ul className="text-xs text-rose-800 list-disc ml-5 space-y-1">
                    <li>Tap the small <strong>flag</strong> icon next to the message: two unique flaggers automatically escalates it to SuperAdmin review</li>
                    <li>SuperAdmin can <strong>suspend a user from the Recovery Room</strong> for a fixed period (24 hours up to 30 days) while behaviour is reviewed</li>
                    <li>Repeat or severe offences result in a permanent ban</li>
                    <li>Suspended users see a clear message explaining the pause and how long it will last</li>
                  </ul>
                </div>
                <NavStep from="Dashboard" action="Bottom bar > Group" to="Recovery Room" />
              </div>
            </Section>

            {/* 15. Badges & Achievements */}
            <Section id="badges" icon={Award} title="14. Badges & Achievements">
              <div className="pt-3 space-y-3">
                <p>Earn badges as you progress through the programme. Badges are awarded for:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Completing daily journal streaks</li>
                  <li>Maintaining consistent mental health check-ins</li>
                  <li>Meeting attendance milestones</li>
                  <li>Week progression achievements</li>
                  <li>Overall programme engagement</li>
                </ul>
                <p>Your most recent badges are shown on the Dashboard. Tap <strong>"All"</strong> next to the badges to see every badge you have earned.</p>
                <NavStep from="Dashboard" action="Badges > All" to="Achievements Page" />
              </div>
            </Section>

            {/* 16. Journey & Progress */}
            <Section id="journey" icon={Map} title="15. Journey & Progress">
              <div className="pt-3 space-y-3">
                <p>Your Journey page shows your full programme timeline:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>View completed weeks and the week you are currently in</li>
                  <li>Tap your <strong>current week</strong> to see its materials, tasks and completion status</li>
                  <li>Access drug screen records (encrypted, for court reporting)</li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="font-bold text-blue-900 mb-1">You can only access the week you are currently in.</p>
                  <p className="text-xs text-blue-800 leading-relaxed">Future weeks are locked. This is by design: Recovery work happens in the present, one week at a time. The programme paces you so you have the support and self-knowledge in place before you encounter the next set of material.</p>
                </div>
                <NavStep from="Dashboard" action="Bottom bar > Journey" to="Journey Page" />
                <NavStep from="Journey" action="Tap your current week" to="Week Detail" />
              </div>
            </Section>

            {/* 15A. Women-Only Safe Space */}
            <Section id="women-only" icon={Heart} title="15A. Women-Only Safe Space (Female Users Only)">
              <div className="pt-3 space-y-3">
                <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-rose-900">
                  <p className="font-bold mb-1">A dedicated space for women in Recovery.</p>
                  <p className="text-xs leading-relaxed">Access is limited to users who declared their gender as <em>Female</em> in Personal History. SuperAdmins can also view for moderation review. Men are not able to access this section.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">What you'll find there</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><strong>The 13th Step</strong>: what it is, why it's harmful and what to do if it happens to you in or out of the Recovery Room</li>
                    <li><strong>"No" is a complete sentence</strong>: practical scripts you can use out loud, no apologies needed</li>
                    <li><strong>Sexual abstinence in early Recovery</strong>: the clinical rationale for the widely-recommended one-year guideline</li>
                    <li><strong>Boundaries 101</strong>: the difference between requests and boundaries; physical / emotional / time / digital</li>
                    <li><strong>Same-gender sponsorship</strong>: why it matters and how the AI Sponsor Bot defaults are set up</li>
                    <li><strong>Reporting inappropriate behaviour</strong>: exactly how to flag, what SuperAdmin can do and your right to silence</li>
                  </ul>
                </div>
                <NavStep from="Dashboard" action="Women-Only card" to="Women-Only Safe Space" />
                <NavStep from="Profile" action="Women-Only Safe Space" to="Women-Only Safe Space" />
              </div>
            </Section>

            {/* 15B. Men-Only Safe Space */}
            <Section id="men-only" icon={MessageSquare} title="15B. Men-Only Safe Space (Male Users Only)">
              <div className="pt-3 space-y-3">
                <div className="bg-sky-50 border border-sky-200 rounded-md p-3 text-sky-900">
                  <p className="font-bold mb-1">A dedicated space for men in Recovery.</p>
                  <p className="text-xs leading-relaxed">Access is limited to users who declared their gender as <em>Male</em> in Personal History. SuperAdmins can also view for moderation review. Women are not able to access this section.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">What you'll find there</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Healthy man-to-man communication</strong>: practical scripts for naming what's actually happening inside, instead of numbing or exploding</li>
                    <li><strong>Don't be a 13th-stepper. Ever.</strong>: explicit, accountability-focused material on not pursuing newcomer women</li>
                    <li><strong>Recognising your own patterns from active addiction</strong>: charm-as-control, the white lie reflex, performative service, score-keeping, anger-as-shortcut</li>
                    <li><strong>Healthy expressions of attraction</strong>: what it looks like to feel attracted to someone and not act on it for a long time</li>
                    <li><strong>Accountability: the man you actually want to be</strong>: daily check-ins with another sober man, prompt amends, no expectation of absolution</li>
                    <li><strong>Saying nothing: the underrated skill</strong>: three breaths, "I need to walk away from this conversation", texts can wait 4 hours</li>
                  </ul>
                </div>
                <NavStep from="Dashboard" action="Men-Only card" to="Men-Only Safe Space" />
                <NavStep from="Profile" action="Men-Only Safe Space" to="Men-Only Safe Space" />
              </div>
            </Section>

            {/* 17. Profile & Settings */}
            <Section id="profile" icon={Settings} title="16. Profile & Settings">
              <div className="pt-3 space-y-3">
                <p>Access your profile through the avatar menu (top-right of the Dashboard).</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Update your name, email and display preferences</li>
                  <li>Change your password</li>
                  <li>View and update sobriety date</li>
                  <li>Manage bot customisation</li>
                </ul>
                <NavStep from="Dashboard" action="Avatar > Profile" to="Profile Page" />
              </div>
            </Section>

            {/* 18. Personal History */}
            <Section id="personal-history" icon={FileText} title="17. Personal History">
              <div className="pt-3 space-y-3">
                <p>A comprehensive data collection form used for research and treatment outcomes (NMDS standards).</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Collects demographic, substance use and treatment history data</li>
                  <li>All data is encrypted and anonymised for research</li>
                  <li>Required during the 2-Week Window (assessment period)</li>
                  <li>You can update it at any time</li>
                </ul>
                <NavStep from="Dashboard" action="Avatar > Personal History" to="Personal History Form" />
              </div>
            </Section>

            {/* 19. SuperAdmin Panel */}
            <Section id="superadmin" icon={Shield} title="18. SuperAdmin Panel (Admins Only)">
              <div className="pt-3 space-y-3">
                <p>Only visible to users with the <strong>superadmin</strong> role.</p>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Users Tab</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>View all registered users</li>
                    <li>See detailed user activity and progress</li>
                    <li>Export criminal justice reports</li>
                    <li>Ban or manage user accounts</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Security Tab</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>View security statistics (blocked IPs, suspicious requests)</li>
                    <li>Manage 2FA (TOTP) for admin accounts</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Content Tab</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li>Edit weekly readings -- change title, description and content text</li>
                    <li>Edit lessons -- update lesson titles, subtitles and content</li>
                    <li>Content supports HTML formatting for rich text</li>
                    <li>Changes go live immediately and users can listen via TTS</li>
                    <li>Create new readings using the "Add Reading" button</li>
                  </ul>
                </div>
                <NavStep from="Dashboard" action="Avatar > SuperAdmin" to="SuperAdmin Panel" />
              </div>
            </Section>

            {/* 20. Back Arrow Behaviour */}
            <Section id="back-arrows" icon={ArrowLeft} title="19. Back Arrow Behaviour">
              <div className="pt-3 space-y-3">
                <p>Every page has a <strong>back arrow</strong> in the top-left corner.</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>The back arrow takes you to the <strong>previous screen</strong> you were on (browser history back)</li>
                  <li>It does <strong>not</strong> always go to the Dashboard -- it follows your actual navigation path</li>
                  <li>The <strong>Home icon</strong> (next to the back arrow) always goes directly to the Dashboard</li>
                </ul>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                  <p className="text-xs text-blue-700"><strong>Example:</strong> If you go Dashboard &rarr; Library &rarr; Reading, pressing the back arrow on the Reading page takes you to Library, not Dashboard. To jump straight to Dashboard, use the Home icon.</p>
                </div>
              </div>
            </Section>

            {/* 21. Emergency Button */}
            <Section id="emergency" icon={AlertTriangle} title="20. SOS Emergency Button: IMPORTANT: Read Before You Need It">
              <div className="pt-3 space-y-3">
                <div className="bg-red-50 border-2 border-red-400 rounded-md p-3">
                  <p className="font-bold text-red-900 mb-1.5 uppercase tracking-wide text-xs">This button does NOT notify anyone automatically.</p>
                  <p className="text-xs text-red-900 leading-relaxed">
                    The red SOS button in the top-right of every page is a <strong>manual dialler</strong>. Tapping it opens a list of phone numbers: your country's emergency services (000 / 911 / 112 / etc.), crisis hotlines and Recovery resources. <strong>You</strong> still have to tap the number to place the call.
                  </p>
                  <ul className="list-disc ml-5 mt-2 text-xs text-red-900 space-y-1">
                    <li>No SMS is sent. No email is sent. No staff is paged. No emergency contact is notified.</li>
                    <li>There is <strong>nothing to "preset"</strong>: no contact list to configure for this button. It simply surfaces public emergency numbers and a `tel:` link.</li>
                    <li>If you are alone, unconscious or unable to tap the screen, this button cannot help you. Call <strong>before</strong> the crisis if you can.</li>
                  </ul>
                </div>
                <p>The red emergency button is visible on <strong>every page</strong> in the top-right area of the header.</p>
                <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
                  <p className="font-bold text-rose-900 mb-1">Always defers to your country's emergency services: no exceptions.</p>
                  <p className="text-xs text-rose-800 leading-relaxed">When you tap the red button, the first action surfaced is the emergency number for your country: <strong>000</strong> (Australia), <strong>911</strong> (US / Canada), <strong>112</strong> (UK / EU), <strong>119</strong> (parts of Asia) or your local equivalent. Crisis hotlines and Recovery resources appear underneath, but emergency services are always front and centre.</p>
                </div>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Available 24/7 regardless of where you are in the app</li>
                  <li>If you are in immediate danger, do not wait: tap the button and call</li>
                  <li>The country detection uses your Personal History data; if you've not yet completed it, the button defaults to a multi-country list</li>
                </ul>
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <p className="font-bold text-amber-900 mb-1">Wellbeing check-in: under review</p>
                  <p className="text-xs text-amber-900 leading-relaxed">
                    The safety-questions section of the Wellbeing check-in is temporarily replaced by a static list of crisis resources while the consent and escalation protocol is under privacy and health-law review. PHQ-9 and GAD-7 continue to run as normal.
                  </p>
                </div>
              </div>
            </Section>

            {/* 22. Mental Health Red Flag Auto-Alerts */}
            <Section id="mental-health-redflag" icon={Heart} title="21. Mental Health Check-ins">
              <div className="pt-3 space-y-3">
                <div className="bg-zinc-50 border-2 border-zinc-300 rounded-md p-3">
                  <p className="font-bold text-zinc-900 mb-1.5 uppercase tracking-wide text-xs">Wellbeing check-ins are private to you.</p>
                  <p className="text-xs text-zinc-800 leading-relaxed">
                    Your PHQ-9 and GAD-7 answers are stored against your own account and surface as trends on your Mental Health page. The check-in does not automatically contact any third party: if scores are high, you are shown crisis lines directly. The historical auto-notification path has been retired pending a full privacy and health-law review.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">The two clinical questionnaires currently in use</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><strong>PHQ-9</strong>: depression screening (9 questions, validated worldwide)</li>
                    <li><strong>GAD-7</strong>: anxiety screening (7 questions, validated worldwide)</li>
                    <li><strong>C-SSRS</strong>: Columbia Suicide Severity Rating Scale. <em>Currently held out of the live flow pending privacy + health-law review.</em> When active, its role is documented in the internal build spec.</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Cadence: when you'll be asked</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Weeks 1–12:</strong> 2–3 check-ins per week, delivered semi-randomly (you won't always know when one is coming)</li>
                    <li><strong>Week 13 onwards:</strong> 1 check-in per week for the rest of the programme</li>
                    <li>You'll see the check-in as a modal on the Dashboard and a card on the Mental Health page</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">The three tiers: what happens to your scores</h4>
                  <ul className="list-disc ml-5 space-y-1">
                    <li><strong>Tier 1 (Green): mild range:</strong> scores logged privately, you see your trend on the Mental Health page. No alerts.</li>
                    <li><strong>Tier 2 (Amber): moderate range:</strong> a supportive message appears in the app encouraging you to reach out to your sponsor / GP. No external alerts.</li>
                    <li><strong>Tier 3 (Red): severe range:</strong> the app immediately surfaces crisis lines (Lifeline 13 11 14, Beyond Blue, Suicide Call Back Service, 000, findahelpline.com). Nothing is sent to any third party.</li>
                  </ul>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="font-bold text-blue-900 mb-1.5">Emergency contacts</p>
                  <p className="text-xs text-blue-900 leading-relaxed mb-2">
                    You can still record emergency contacts in your Personal History: they remain available to you as your own quick-dial list. The historical auto-notification feature that emailed them on a Tier-3 result has been removed. Any future version of a contact-notification feature will require your explicit, documented consent before it goes live.
                  </p>
                  <NavStep from="Avatar" action="Personal History" to="Emergency Contacts section" />
                </div>

                <div>
                  <h4 className="font-semibold text-zinc-800 mb-1">Honesty matters</h4>
                  <p>These three questionnaires only work if you answer them honestly. They are <em>not</em> tests you can fail. Under-reporting how you feel doesn't make you stronger: it makes the safety net larger-holed. Your raw answers stay private; only the risk tier is shared with anyone outside you.</p>
                </div>

                <NavStep from="Dashboard" action="Mental Health card" to="Mental Health Check-in" />
                <NavStep from="Daily Tasks" action="Mental Health" to="Mental Health Check-in" />
              </div>
            </Section>
          </div>

          {/* Footer note */}
          <div className="mt-8 text-center text-xs text-zinc-400">
            <p>Axiom Recovery Programme -- Your 24/7 Digital Sponsor</p>
            <p className="mt-1">For questions or support, contact your programme administrator.</p>
          </div>
        </div>
      </main>

      <BackToTopButton />
      <Footer />
    </div>
  );
}

export default UserManualPage;
